/**
 * @hook useTourGuide
 * @description A custom hook for creating and managing guided tours throughout the application.
 * Provides a consistent way to define and control tour steps across different components.
 *
 * @example
 * Basic usage in a component
 * const { startTour, isOpen, currentStep } = useTourGuide({
 *   steps: tourSteps,
 *   onFinish: () => console.log('Tour finished'),
 * });
 *
 * @example
 * With all options
 * const { startTour, isOpen, currentStep, goToStep } = useTourGuide({
 *   steps: tourSteps,
 *   onFinish: handleFinish,
 *   onSkip: handleSkip,
 *   startAtStep: 0,
 *   disableOverlay: false,
 *   spotlightClicks: false,
 * });
 */

import { useTour } from '@/contexts/TourContext';
import { useCallback, useState } from 'react';

const waitForElement = (selector, timeout = 2000) => {
   return new Promise((resolve) => {
      // Check if element already exists
      const element = document.querySelector(selector);
      if (element) {
         return resolve(element);
      }

      let timeoutId;
      const observer = new MutationObserver(() => {
         const element = document.querySelector(selector);
         if (element) {
            clearTimeout(timeoutId);
            observer.disconnect();
            resolve(element);
         }
      });

      // Start observing with a more focused approach
      observer.observe(document.body, {
         childList: true,
         subtree: true,
         attributes: false, // We don't need to watch attributes
         characterData: false // We don't need to watch text changes
      });

      // Set timeout
      timeoutId = setTimeout(() => {
         observer.disconnect();
         resolve(null);
      }, timeout);
   });
};

const useTourGuide = ({
   tourId,
   onFinish,
   onSkip,
   startAtStep = 0,
   disableOverlay = false,
   spotlightClicks = false,
}) => {
   const { startTour } = useTour();
   const [isOpen, setIsOpen] = useState(false);
   const [currentStep, setCurrentStep] = useState(startAtStep);

   // Define common tour steps that can be reused across different pages
   const commonTourSteps = {
      mainTabs: {
         target: '.main-tabs',
         content: 'Switch between different sections using these main tabs',
         placement: 'bottom',
         disableBeacon: true,
      },
      subTabs: {
         target: '.sub-tabs',
         content: 'View different statuses or categories using these sub-tabs',
         placement: 'bottom',
      },
      filters: {
         target: '.applications-filters',
         content: 'Filter and search through your items',
         placement: 'bottom',
      },
      table: {
         target: '.applications-table',
         content: 'View all your items and their details in this table',
         placement: 'top',
      },
      actions: {
         target: '.table-actions',
         content: 'Perform various actions on your items',
         placement: 'left',
      },
      viewAction: {
         target: '.view-action',
         content: 'Click here to view the complete details of your application',
         placement: 'left',
      },
      editAction: {
         target: '.edit-action',
         content: 'Click here to modify your application details',
         placement: 'left',
      },
      unsubmitAction: {
         target: '.unsubmit-action',
         content: 'Click here to unsubmit your application and make changes',
         placement: 'left',
      },
      pagination: {
         target: '.pagination-controls',
         content: 'Navigate through multiple pages',
         placement: 'top',
         disableOverlay: true,
      }
   };

   // Helper function to create tour steps with common configurations
   const createTourSteps = useCallback((steps) => {
      return steps.map(step => {
         if (typeof step === 'string' && commonTourSteps[step]) {
            return commonTourSteps[step];
         }
         return {
            ...step,
            disableBeacon: step.disableBeacon ?? false,
            disableOverlay: step.disableOverlay ?? disableOverlay,
            spotlightClicks: step.spotlightClicks ?? spotlightClicks,
         };
      });
   }, [disableOverlay, spotlightClicks]);

   // Helper function to verify all elements exist
   const verifyElements = async (steps) => {
      for (const step of steps) {
         const target = typeof step === 'string' ?
            commonTourSteps[step].target :
            step.target;

         const element = await waitForElement(target);
         if (!element) {
            console.warn(`Tour target not found: ${target}`);
            return false;
         }
      }
      return true;
   };

   // Start the tour with configured steps
   const initiateTour = useCallback(async (steps) => {
      const configuredSteps = createTourSteps(steps);

      try {
         // Quick check for first element before starting verification
         const firstStep = configuredSteps[0];
         if (!firstStep) {
            console.warn('No tour steps provided');
            return;
         }

         const firstTarget = typeof firstStep === 'string' ?
            commonTourSteps[firstStep].target :
            firstStep.target;

         // Wait for first element with a shorter timeout
         const firstElement = await waitForElement(firstTarget, 1000);
         if (!firstElement) {
            console.warn('First tour element not found, tour may not start correctly');
            return;
         }

         // Verify all elements exist before starting
         const allElements = await Promise.all(
            configuredSteps.map(async (step) => {
               const target = typeof step === 'string' ?
                  commonTourSteps[step].target :
                  step.target;
               return await waitForElement(target, 500);
            })
         );

         // Filter out steps where elements don't exist
         const availableSteps = configuredSteps.filter((_, index) => allElements[index]);

         if (availableSteps.length !== configuredSteps.length) {
            console.warn(`Some tour elements are not mounted. Tour will run with ${availableSteps.length} of ${configuredSteps.length} steps.`);
         }

         // Start the tour with available steps
         setIsOpen(true);
         setCurrentStep(startAtStep);
         startTour(availableSteps, tourId);
      } catch (error) {
         console.error('Error starting tour:', error);
      }
   }, [createTourSteps, startAtStep, tourId, startTour]);

   // Go to a specific step
   const goToStep = useCallback((step) => {
      setCurrentStep(step);
   }, []);

   // Handle tour finish
   const handleFinish = useCallback(() => {
      setIsOpen(false);
      if (onFinish) {
         onFinish();
      }
   }, [onFinish]);

   // Handle tour skip
   const handleSkip = useCallback(() => {
      setIsOpen(false);
      if (onSkip) {
         onSkip();
      }
   }, [onSkip]);

   return {
      startTour: initiateTour,
      isOpen,
      currentStep,
      goToStep,
      commonTourSteps,
      createTourSteps,
   };
};

export default useTourGuide;